import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWithdraw } from '../../hooks/useWithdraw';
import SuccessModal from '../../components/common/UI/SuccessModal';
import './Withdraw.css';

// ========================================
// SECURITY VALIDATION CLASS
// ========================================
class WithdrawSecurityValidator {
  // SQL injection patterns
  static SQL_PATTERNS = [
    /\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|EXECUTE|UNION|SCRIPT)\b/i,
    /--/,
    /\/\*/,
    /\*\//,
    /;/,
    /xp_/i,
    /sp_/i,
  ];

  // XSS patterns
  static XSS_PATTERNS = [
    /<script/i,
    /javascript:/i,
    /onerror=/i,
    /onclick=/i,
    /onload=/i,
    /<iframe/i,
    /<object/i,
    /<embed/i,
  ];

  static containsSQLInjection(value) {
    if (typeof value !== 'string') return false;
    return this.SQL_PATTERNS.some(pattern => pattern.test(value));
  }

  static containsXSS(value) {
    if (typeof value !== 'string') return false;
    return this.XSS_PATTERNS.some(pattern => pattern.test(value));
  }

  /**
   * Validate crypto wallet address
   * - 26-50 characters
   * - Alphanumeric only
   * - No SQL injection or XSS
   */
  static validateWalletAddress(address) {
    if (!address) {
      throw new Error('Wallet address is required');
    }

    const trimmed = address.trim();

    // Check for SQL injection
    if (this.containsSQLInjection(trimmed)) {
      throw new Error('Invalid wallet address format. SQL queries not allowed.');
    }

    // Check for XSS
    if (this.containsXSS(trimmed)) {
      throw new Error('Invalid wallet address format. HTML/Script tags not allowed.');
    }

    // Check length
    if (trimmed.length < 26 || trimmed.length > 50) {
      throw new Error('Wallet address must be 26-50 characters long');
    }

    // Check alphanumeric only
    if (!/^[0-9a-zA-Z]+$/.test(trimmed)) {
      throw new Error('Wallet address must contain only letters and numbers');
    }

    return trimmed;
  }

  /**
   * Validate withdrawal amount
   */
  static validateAmount(amount) {
    if (!amount) {
      throw new Error('Amount is required');
    }

    const numAmount = parseFloat(amount);

    if (isNaN(numAmount)) {
      throw new Error('Invalid amount format');
    }

    if (numAmount < 1000) {
      throw new Error('Minimum withdrawal amount is ₦1,000');
    }

    if (numAmount > 100000000) {
      throw new Error('Amount exceeds maximum limit');
    }

    return numAmount;
  }

  /**
   * Validate network selection
   */
  static validateNetwork(network) {
    if (!network) {
      throw new Error('Please select a network');
    }

    const validNetworks = ['bsc', 'ethereum', 'tron', 'solana'];
    if (!validNetworks.includes(network.toLowerCase())) {
      throw new Error('Invalid network selected');
    }

    return network;
  }
}

const CryptoWithdrawal = () => {
  const navigate = useNavigate();
  const { createCryptoWithdrawal, loading, error, isSuccess, successDetails, hideSuccess } = useWithdraw();

  const [formData, setFormData] = useState({
    amount: '',
    walletAddress: '',
    cryptocurrency: 'USDT',
    network: ''
  });

  const [validationErrors, setValidationErrors] = useState({});

  // Updated networks with USDT prefix
  const networks = [
    { id: 'bsc', label: 'USDT BNB Smart Chain (BEP20)' },
    { id: 'ethereum', label: 'USDT Ethereum (ERC20)' },
    { id: 'tron', label: 'USDT Tron (TRC20)' },
    { id: 'solana', label: 'USDT Solana' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleNetworkSelect = (networkId) => {
    setFormData(prev => ({
      ...prev,
      network: networkId
    }));

    // Clear network validation error
    if (validationErrors.network) {
      setValidationErrors(prev => ({
        ...prev,
        network: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    try {
      // Validate amount
      WithdrawSecurityValidator.validateAmount(formData.amount);
    } catch (error) {
      errors.amount = error.message;
    }

    try {
      // Validate wallet address
      WithdrawSecurityValidator.validateWalletAddress(formData.walletAddress);
    } catch (error) {
      errors.walletAddress = error.message;
    }

    try {
      // Validate network
      WithdrawSecurityValidator.validateNetwork(formData.network);
    } catch (error) {
      errors.network = error.message;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      console.log('❌ Form validation failed:', validationErrors);
      return;
    }

    try {
      const withdrawalData = {
        amount: parseFloat(formData.amount),
        cryptocurrency: formData.cryptocurrency,
        network: formData.network,
        wallet_address: formData.walletAddress.trim()
      };

      await createCryptoWithdrawal(withdrawalData);

      // Clear form on success
      setFormData({
        amount: '',
        walletAddress: '',
        cryptocurrency: 'USDT',
        network: ''
      });
      setValidationErrors({});

    } catch (err) {
      console.error('❌ Withdrawal failed:', err);
    }
  };

  return (
    <div className="withdraw-page">
      <div className="withdraw-header">
        <button className="back-link" onClick={() => navigate(-1)}>
          <span className="back-arrow">←</span> Back to Dashboard
        </button>
      </div>

      <div className="withdraw-header-mobile">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <span className="back-icon">←</span> Back
        </button>
      </div>

      <div className="withdraw-container">
        <div className="withdraw-card">
          <h1 className="withdraw-title">Crypto Withdrawal</h1>

          <form onSubmit={handleSubmit} className="withdraw-form">
            <div className="form-group">
              <label>Amount (in naira) <span className="required">*</span></label>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="0.00"
                step="0.000001"
                min="1000"
                className="form-input"
              />
              {validationErrors.amount && (
                <p className="validation-error">⚠️ {validationErrors.amount}</p>
              )}
            </div>

            <div className="form-group">
              <label>Wallet Address <span className="required">*</span></label>
              <input
                type="text"
                name="walletAddress"
                value={formData.walletAddress}
                onChange={handleChange}
                placeholder="Enter your wallet address"
                className="form-input"
                maxLength={50}
              />
              {validationErrors.walletAddress && (
                <p className="validation-error">⚠️ {validationErrors.walletAddress}</p>
              )}
              <p className="input-hint">
                Double-check your wallet address before submitting (26-50 characters, alphanumeric only)
              </p>
            </div>

            <div className="form-group">
              <label>Select Blockchain Network <span className="required">*</span></label>
              <div className="network-grid">
                {networks.map((network) => (
                  <button
                    key={network.id}
                    type="button"
                    className={`network-btn ${formData.network === network.id ? 'selected' : ''}`}
                    onClick={() => handleNetworkSelect(network.id)}
                  >
                    {network.label}
                  </button>
                ))}
              </div>
              {validationErrors.network && (
                <p className="validation-error">⚠️ {validationErrors.network}</p>
              )}
            </div>

            {error && <div className="error-message">{error}</div>}

            <button 
              type="submit" 
              className="submit-btn"
              disabled={loading || !formData.network}
            >
              {loading ? 'Submitting...' : 'Submit Withdrawal'}
            </button>
          </form>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={isSuccess}
        onClose={hideSuccess}
        title="Crypto Withdrawal Request Submitted!"
        message="Your cryptocurrency withdrawal request has been received and is being processed."
        details={successDetails}
        onContinue={() => {
          hideSuccess();
          navigate('/transaction-history');
        }}
      />
    </div>
  );
};

export default CryptoWithdrawal;